import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@my-saas/ui";
import { TrashIcon, UserPlusIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router";

import { toast } from "@/client/hooks/use-toast";
import { useMemberMutations, useMembers } from "@/client/services/tenant-config";

const ROLES = ["USER", "TENANT_ADMIN", "TENANT_SUPERADMIN"] as const;
type Role = (typeof ROLES)[number];

export const TenantMembersPage = () => {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { data: members, isPending } = useMembers(tenantSlug);
  const { invite, update, remove } = useMemberMutations(tenantSlug);
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState<Role>("USER");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.includes("@")) return;
    try {
      await invite.mutateAsync({
        email: email.trim(),
        role,
        displayName: displayName.trim() || undefined,
        phone: phone.trim() || undefined,
      });
      toast({ title: "Member invited", description: email });
      setEmail("");
      setDisplayName("");
      setPhone("");
      setRole("USER");
    } catch (e) {
      toast({
        variant: "error",
        title: "Invite failed",
        description: (e as Error).message,
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Members · {tenantSlug}</title>
      </Helmet>

      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">Members</h1>
          <p className="text-sm text-muted-foreground">
            People with access to this tenant. New invitees get a stub account
            with mustChangePassword=true; they complete sign-up via the
            forgot-password flow.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Invite member</CardTitle>
            <CardDescription>By email. Idempotent — re-inviting updates the role.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-[2fr,2fr,1.5fr,1fr,auto]">
              <div className="space-y-1">
                <Label className="text-xs">Email</Label>
                <Input
                  type="email"
                  required
                  placeholder="member@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Display name</Label>
                <Input
                  placeholder="optional"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Phone</Label>
                <Input
                  placeholder="optional"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Role</Label>
                <select
                  className="w-full rounded border bg-background px-2 py-1.5 text-sm"
                  value={role}
                  onChange={(e) => setRole(e.target.value as Role)}
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
              <Button type="submit" disabled={invite.isPending} className="self-end">
                <UserPlusIcon className="mr-2 size-4" />
                Invite
              </Button>
            </form>
          </CardContent>
        </Card>

        {isPending && <p className="text-sm text-muted-foreground">Loading…</p>}

        <div className="space-y-2">
          {members?.map((m) => (
            <Card key={m.id}>
              <CardContent className="grid grid-cols-1 items-center gap-3 p-4 sm:grid-cols-[2fr,1.5fr,1fr,auto,auto]">
                <div>
                  <div className="font-medium">{m.user.name || m.user.email}</div>
                  <div className="text-xs text-muted-foreground">{m.user.email}</div>
                </div>
                <div className="text-xs text-muted-foreground">
                  {m.displayName && <div>{m.displayName}</div>}
                  {m.phone && <div className="font-mono">{m.phone}</div>}
                </div>
                <select
                  className="rounded border bg-background px-2 py-1 text-sm"
                  value={m.role}
                  onChange={(e) =>
                    update.mutate({ membershipId: m.id, role: e.target.value as Role })
                  }
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
                <div className="flex gap-1">
                  {!m.user.emailVerified && <Badge variant="secondary">unverified</Badge>}
                  {m.mustChangePassword && <Badge variant="secondary">must reset pw</Badge>}
                </div>
                <Button
                  size="sm"
                  variant="error"
                  onClick={() => {
                    if (confirm(`Remove ${m.user.email} from this tenant?`)) remove.mutate(m.id);
                  }}
                >
                  <TrashIcon className="size-4" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </>
  );
};
